import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { formatTime, getTodayTime, saveTodayTime, getTotalTime, getTotalTodayTime, getTotalYesterdayTime, getTotalThisWeekTime, getTodayKey } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import Accordion from './common/Accordion';
import { fetchAllVocabWords } from '../services/wordBankService';
import type { VocabWord } from '../types/vocab';
import { useSessionTracker } from '../hooks/useSessionTracker';
import { useSubscription } from '../hooks/useSubscription';
import PaywallModal from './PaywallModal';
import { supabase } from '../services/supabaseClient';

const FREE_SYNONYM_SESSION_LIMIT = 3;

const PRIMARY = 'var(--color-primary)';
const SURFACE = 'var(--color-surface)';
const BORDER = 'var(--color-border)';
const TEXT_MUTED = 'var(--color-text-muted)';

type WordData = VocabWord;

interface SynonymRecord {
  word: string;
  definition: string;
  allSynonymsMatched: boolean;
}

interface ReportRecord {
  word: string;
  type: 'definition' | 'synonym';
  reportedDefinition?: string;
  reportedSynonym?: string;
  note?: string;
  timestamp: number;
}

const SynonymGame: React.FC = () => {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const {
    isSessionActive,
    sessionNumber,
    lastSessionCardCount,
    lifetimeSeenCount,
    startSession,
    recordCardSeen,
    endSession,
  } = useSessionTracker('synonym');
  const [words, setWords] = useState<WordData[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [matchedSynonyms, setMatchedSynonyms] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [wrongSelections, setWrongSelections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [completedWords, setCompletedWords] = useState<SynonymRecord[]>([]);
  const [incompleteWords, setIncompleteWords] = useState<SynonymRecord[]>([]);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [reviewMode, setReviewMode] = useState(false); // Toggle for reviewing only incomplete words
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportNote, setReportNote] = useState('');
  const [reportingSynonym, setReportingSynonym] = useState<string>('');
  const [reportedIssues, setReportedIssues] = useState<ReportRecord[]>([]);
  const [isShuffled, setIsShuffled] = useState(false);
  const [, setElapsedTime] = useState(0); // Time in seconds
  const [todayTime, setTodayTime] = useState(0); // Time spent today
  const [totalTime, setTotalTime] = useState(0); // Total time across all modes
  const [totalTodayTime, setTotalTodayTime] = useState(0); // Total time today across all modes
  const sessionStartTimeRef = useRef<number | null>(null);
  const todaySessionStartRef = useRef<number | null>(null);
  const initialSavedTimeRef = useRef<number>(0); // Track initial saved time to avoid double counting
  const initialTodayTimeRef = useRef<number>(0); // Track initial saved today time
  const [pendingWordIndex, setPendingWordIndex] = useState<number | null>(null);
  const [jumpToNumber, setJumpToNumber] = useState('');
  const [shuffleOrder, setShuffleOrder] = useState<number[] | null>(null);

  const saveSynonymProgressToSupabase = async (word: WordData, completed: boolean) => {
    if (!user) return;
    try {
      await dataService.synonymProgress.upsert({
        user_id: user.id,
        word_id: word.ID || word.Word,
        word: word.Word,
        completed,
        timestamp: Date.now(),
        date: getTodayKey()
      });
    } catch (e) {
      console.error('Error saving synonym progress to Supabase:', e);
    }
  };

  const clearSupabaseProgress = async () => {
    if (!user) return;
    try {
      await Promise.all([
        dataService.synonymProgress.deleteAll(user.id),
        dataService.reportedIssues.deleteByGameMode(user.id, 'synonym'),
        dataService.userProgressState.delete(user.id, 'synonym', 'last_word_index')
      ]);
    } catch (e) {
      console.error('Error clearing synonym data in Supabase:', e);
    }
  };

  const handleClearProgress = async () => {
    if (!window.confirm('Are you sure you want to clear all saved progress? This cannot be undone.')) {
      return;
    }

    setCompletedWords([]);
    setIncompleteWords([]);
    try {
      localStorage.removeItem('gre_synonym_completed');
      localStorage.removeItem('gre_synonym_incomplete');
      localStorage.removeItem('gre_synonym_last_word_index');
    } catch (e) {
      console.error('Error clearing synonym progress from localStorage:', e);
    }

    if (user) {
      await clearSupabaseProgress();
      try {
        await dataService.userProgressState.delete(user.id, 'synonym', 'last_word_index');
      } catch (e) {
        console.error('Error clearing synonym progress state in Supabase:', e);
      }
    }
  };

  // Load data (local first, then Supabase if available)
  useEffect(() => {
    let isMounted = true;

    const loadFromLocalStorage = () => {
      try {
        const savedCompleted = localStorage.getItem('gre_synonym_completed');
        const savedIncomplete = localStorage.getItem('gre_synonym_incomplete');
        const savedReports = localStorage.getItem('gre_reported_issues');
        
        if (savedCompleted) {
          try {
            const parsed = JSON.parse(savedCompleted);
            if (isMounted) {
              setCompletedWords(parsed);
            }
          } catch (e) {
            console.error('Error parsing completed words from localStorage:', e);
          }
        }
        
        if (savedIncomplete) {
          try {
            const parsed = JSON.parse(savedIncomplete);
            if (isMounted) {
              setIncompleteWords(parsed);
            }
          } catch (e) {
            console.error('Error parsing incomplete words from localStorage:', e);
          }
        }

        if (savedReports) {
          try {
            const parsed = JSON.parse(savedReports);
            if (isMounted) {
              setReportedIssues(parsed);
            }
          } catch (e) {
            console.error('Error parsing reported issues from localStorage:', e);
          }
        }

        const savedIndex = localStorage.getItem('gre_synonym_last_word_index');
        if (savedIndex) {
          const parsedIndex = parseInt(savedIndex, 10);
          if (!isNaN(parsedIndex) && parsedIndex >= 0) {
            setPendingWordIndex(parsedIndex);
          }
        }

        const savedTime = localStorage.getItem('gre_synonym_time');
        if (savedTime) {
          try {
            const savedTimeSeconds = parseInt(savedTime, 10);
            if (!isNaN(savedTimeSeconds) && savedTimeSeconds >= 0) {
              initialSavedTimeRef.current = savedTimeSeconds;
              setElapsedTime(savedTimeSeconds);
            }
          } catch (e) {
            console.error('Error parsing saved time:', e);
          }
        }
        
        const todayTimeValue = getTodayTime('synonym');
        initialTodayTimeRef.current = todayTimeValue;
        setTodayTime(todayTimeValue);
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        sessionStartTimeRef.current = Date.now();
        todaySessionStartRef.current = Date.now();
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }
    };

    const loadFromSupabase = async () => {
      if (!user) return;
      try {
        const [
          { data: progressData, error: progressError },
          { data: reportsData, error: reportsError },
          { data: progressStateData, error: progressStateError }
        ] = await Promise.all([
          dataService.synonymProgress.getAll(user.id),
          dataService.reportedIssues.getByGameMode(user.id, 'synonym'),
          dataService.userProgressState.get(user.id, 'synonym', 'last_word_index')
        ]);

        if (!isMounted) return;

        if (progressError) {
          console.error('Error loading synonym progress from Supabase:', progressError.message);
        } else if (progressData) {
          const supaCompleted: SynonymRecord[] = [];
          const supaIncomplete: SynonymRecord[] = [];

          progressData.forEach((record: any) => {
            const rec: SynonymRecord = {
              word: record.word,
              definition: '',
              allSynonymsMatched: !!record.completed
            };
            if (record.completed) {
              supaCompleted.push(rec);
            } else {
              supaIncomplete.push(rec);
            }
          });

          setCompletedWords(supaCompleted);
          setIncompleteWords(supaIncomplete);
          try {
            localStorage.setItem('gre_synonym_completed', JSON.stringify(supaCompleted));
            localStorage.setItem('gre_synonym_incomplete', JSON.stringify(supaIncomplete));
          } catch (e) {
            console.error('Error saving synonym progress to localStorage:', e);
          }
        }

        if (progressStateError) {
          console.error('Error loading synonym progress state from Supabase:', progressStateError.message);
        } else if (progressStateData && progressStateData.state_value) {
          const index = parseInt(progressStateData.state_value, 10);
          if (!isNaN(index) && index >= 0) {
            setPendingWordIndex(index);
          }
        }

        if (reportsError) {
          console.error('Error loading synonym reports from Supabase:', reportsError.message);
        } else if (reportsData) {
          const mappedReports: ReportRecord[] = reportsData.map((issue: any) => ({
            word: issue.word,
            type: 'synonym',
            reportedSynonym: issue.reported_synonym || undefined,
            note: issue.note || undefined,
            timestamp: issue.timestamp || Date.now()
          }));
          setReportedIssues(mappedReports);
          try {
            localStorage.setItem('gre_reported_issues', JSON.stringify(mappedReports));
          } catch (e) {
            console.error('Error saving reported issues locally:', e);
          }
        }
      } catch (e) {
        console.error('Unexpected error loading Synonym data from Supabase:', e);
      }
    };

    loadFromLocalStorage();
    if (user) {
      loadFromSupabase();
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Timer effect - update every second
  useEffect(() => {
    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
    if (!todaySessionStartRef.current) {
      todaySessionStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (sessionStartTimeRef.current && todaySessionStartRef.current) {
        const now = Date.now();
        
        // Mode-specific timer - use initial saved time + current session elapsed
        const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
        const totalElapsed = initialSavedTimeRef.current + sessionElapsed;
        setElapsedTime(totalElapsed);
        
        // Today's timer - use initial saved today time + current session elapsed
        const todaySessionElapsed = Math.floor((now - todaySessionStartRef.current) / 1000);
        const totalTodayElapsed = initialTodayTimeRef.current + todaySessionElapsed;
        setTodayTime(totalTodayElapsed);
        
        // Total time across all modes (recalculate from localStorage)
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        
        // Save to localStorage every 5 seconds
        if (sessionElapsed % 5 === 0 && sessionElapsed > 0) {
          try {
            localStorage.setItem('gre_synonym_time', totalElapsed.toString());
            saveTodayTime('synonym', totalTodayElapsed);
            // Update initial saved time to the new total, then reset session start
            initialSavedTimeRef.current = totalElapsed;
            initialTodayTimeRef.current = totalTodayElapsed;
            sessionStartTimeRef.current = Date.now();
            todaySessionStartRef.current = Date.now();
          } catch (e) {
            console.error('Error saving time to localStorage:', e);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Save completed and incomplete words to localStorage
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    
    try {
      localStorage.setItem('gre_synonym_completed', JSON.stringify(completedWords));
      localStorage.setItem('gre_synonym_incomplete', JSON.stringify(incompleteWords));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }, [completedWords, incompleteWords, isInitialMount]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetchAllVocabWords()
      .then(({ data, error }) => {
        if (!isMounted) return;

        if (error) {
          console.error('Error loading vocab from Supabase:', error.message);
          setLoading(false);
          return;
        }

        if (!data || data.length === 0) {
          console.error('No vocab words found in Supabase.');
          setLoading(false);
          return;
        }

        const wordsWithSynonyms = data.filter(word => {
          const synonymsStr =
            word['Similar words category'] ||
            word['Similar words '] ||
            (word as any)['Similar words'] ||
            '';
          const hasSynonyms =
            synonymsStr &&
            typeof synonymsStr === 'string' &&
            synonymsStr.trim() !== '' &&
            synonymsStr.trim() !== ',';
          return hasSynonyms;
        });

        if (wordsWithSynonyms.length === 0) {
          console.error('No words with synonyms found.');
          setLoading(false);
          return;
        }

        const sortedData = wordsWithSynonyms.sort((a, b) => a.Word.localeCompare(b.Word));
        setWords(sortedData);
        setLoading(false);
      })
      .catch(error => {
        if (!isMounted) return;
        console.error('Unexpected error loading vocab from Supabase:', error);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (pendingWordIndex === null) return;
    if (words.length === 0) return;
    if (reviewMode || isShuffled) return;

    const clamped = Math.min(Math.max(pendingWordIndex, 0), words.length - 1);
    setCurrentWordIndex(clamped);
    setPendingWordIndex(null);
  }, [pendingWordIndex, words.length, reviewMode, isShuffled]);

  const baseWords = useMemo(() => {
    if (reviewMode && incompleteWords.length > 0) {
      const incompleteWordNames = incompleteWords.map(record => record.word);
      return words.filter(word => incompleteWordNames.includes(word.Word));
    }
    return words;
  }, [reviewMode, incompleteWords, words]);

  useEffect(() => {
    if (!isShuffled) {
      setShuffleOrder(null);
      return;
    }
    if (baseWords.length === 0) {
      setShuffleOrder(null);
      return;
    }

    const order = Array.from({ length: baseWords.length }, (_, index) => index);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    setShuffleOrder(order);
    setCurrentWordIndex(0);
  }, [isShuffled, baseWords]);

  // Get words to use - either base order or shuffled using persisted order
  const wordsToUse = useMemo(() => {
    if (isShuffled && shuffleOrder && shuffleOrder.length === baseWords.length) {
      return shuffleOrder.map(index => baseWords[index]);
    }
    return baseWords;
  }, [baseWords, isShuffled, shuffleOrder]);

  // Reset to first word when entering/exiting review mode or toggling shuffle
  // NOTE: wordsToUse.length intentionally omitted — adding it caused the restored
  // position to be wiped when words loaded (length 0→N) on every mount.
  useEffect(() => {
    if (wordsToUse.length > 0) {
      setCurrentWordIndex(0);
      setMatchedSynonyms([]);
      setSelectedWords([]);
      setWrongSelections([]);
      setShowResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode, isShuffled]);

  useEffect(() => {
    if (reviewMode || isShuffled) return;
    if (wordsToUse.length === 0) return;

    try {
      localStorage.setItem('gre_synonym_last_word_index', currentWordIndex.toString());
    } catch (e) {
      console.error('Error saving synonym index to localStorage:', e);
    }

    if (!user) return;

    dataService.userProgressState
      .upsert({
        user_id: user.id,
        game_mode: 'synonym',
        state_key: 'last_word_index',
        state_value: currentWordIndex.toString()
      })
      .catch(error => {
        console.error('Error saving synonym index to Supabase:', error);
      });
  }, [currentWordIndex, reviewMode, isShuffled, wordsToUse.length, user]);


  useEffect(() => {
    return () => {
      if (isSessionActive) endSession(0);
    };
  }, [isSessionActive, endSession]);

  const getSynonyms = useCallback((): string[] => {
    if (!wordsToUse[currentWordIndex]) return [];
    const wordData = wordsToUse[currentWordIndex];
    
    // The synonyms are in "Similar words category" column, not "Similar words "
    const synonymsStr = wordData['Similar words category'] || wordData['Similar words '] || (wordData as any)['Similar words'] || '';
    
    if (!synonymsStr || synonymsStr.trim() === '') {
      return [];
    }
    const synonyms = synonymsStr.split(';').map((s: string) => s.trim()).filter((s: string) => s !== '' && s !== ',');
    return synonyms;
  }, [wordsToUse, currentWordIndex]);

  // Generate a list of words to choose from (synonyms + distractors)
  const wordOptions = useMemo(() => {
    if (wordsToUse.length === 0) return [];
    
    const currentWord = wordsToUse[currentWordIndex];
    const synonyms = getSynonyms();
    
    // If no synonyms, return empty array
    if (synonyms.length === 0) {
      return [];
    }
    
    const allOptions: string[] = [...synonyms];
    
    // Add some random wrong words as distractors
    const usedIndices = new Set([currentWordIndex]);
    const wrongWords: string[] = [];
    
    // Get 5-8 distractors (wrong words)
    while (wrongWords.length < Math.min(8, synonyms.length * 2) && usedIndices.size < words.length) {
      const randomIndex = Math.floor(Math.random() * words.length);
      if (!usedIndices.has(randomIndex)) {
        const wrongWord = words[randomIndex].Word;
        // Make sure it's not the same as the current word or already a synonym
        if (wrongWord !== currentWord.Word && !synonyms.includes(wrongWord)) {
          wrongWords.push(wrongWord);
          usedIndices.add(randomIndex);
        }
      }
    }
    
    allOptions.push(...wrongWords);
    
    // Shuffle the options
    for (let i = allOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
    }
    
    return allOptions;
  }, [wordsToUse, currentWordIndex, words, getSynonyms]);

  const handleWordSelect = (word: string) => {
    if (showResult) return;
    
    const normalizedWord = word.trim();
    
    // Toggle selection - just track which words are selected, don't show if correct/wrong yet
    if (selectedWords.includes(normalizedWord)) {
      // Deselect
      setSelectedWords(prev => prev.filter(w => w !== normalizedWord));
    } else {
      // Select
      setSelectedWords([...selectedWords, normalizedWord]);
    }
  };

  const nextWord = () => {
    setCurrentWordIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % wordsToUse.length;
      return nextIndex;
    });
    setMatchedSynonyms([]);
    setSelectedWords([]);
    setWrongSelections([]);
    setShowResult(false);
  };

  const jumpToCard = () => {
    if (wordsToUse.length === 0) return;
    const target = parseInt(jumpToNumber, 10);
    if (isNaN(target)) return;

    const clamped = Math.min(Math.max(target - 1, 0), wordsToUse.length - 1);
    setCurrentWordIndex(clamped);
    setMatchedSynonyms([]);
    setSelectedWords([]);
    setWrongSelections([]);
    setShowResult(false);
    setJumpToNumber('');
  };

  const handleJumpInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      jumpToCard();
    }
  };

  const handleReportSynonym = (synonym: string) => {
    setReportingSynonym(synonym);
    setShowReportModal(true);
    setReportNote('');
  };

  const submitReport = async () => {
    const word = wordsToUse[currentWordIndex];
    if (!word) return;
    
    const report: ReportRecord = {
      word: word.Word,
      type: 'synonym',
      reportedSynonym: reportingSynonym,
      note: reportNote.trim(),
      timestamp: Date.now()
    };

    const updatedReports = [...reportedIssues, report];
    setReportedIssues(updatedReports);
    
    try {
      localStorage.setItem('gre_reported_issues', JSON.stringify(updatedReports));
    } catch (e) {
      console.error('Error saving report to localStorage:', e);
    }

    if (user) {
      try {
        await dataService.reportedIssues.insert({
          user_id: user.id,
          game_mode: 'synonym',
          word_id: word.ID || word.Word,
          word: word.Word,
          note: reportNote.trim() || undefined,
          timestamp: report.timestamp,
          date: getTodayKey()
        });
      } catch (e) {
        console.error('Error saving synonym report to Supabase:', e);
      }
    }

    setShowReportModal(false);
    setReportNote('');
    setReportingSynonym('');
    alert('Thank you for reporting! Your feedback has been saved.');
  };

  const checkCompletion = () => {
    const synonyms = getSynonyms();
    
    // Check which selected words are correct synonyms
    const correctSelections: string[] = [];
    const incorrectSelections: string[] = [];
    
    selectedWords.forEach(word => {
      const isSynonym = synonyms.some(s => s.trim() === word.trim());
      if (isSynonym) {
        correctSelections.push(word);
      } else {
        incorrectSelections.push(word);
      }
    });
    
    // Check which synonyms were missed (for potential future use)
    // const missedSynonyms = synonyms.filter(syn => !selectedWords.includes(syn.trim()));
    
    // Update matched synonyms and wrong selections for display
    setMatchedSynonyms(correctSelections);
    setWrongSelections(incorrectSelections);
    
    // Check if all synonyms were matched
    const allMatched = synonyms.every(syn => correctSelections.includes(syn.trim()));
    setShowResult(true);
    
    // Track the result
    const currentWord = wordsToUse[currentWordIndex];
    if (currentWord) {
      const record: SynonymRecord = {
        word: currentWord.Word,
        definition: currentWord.Definition,
        allSynonymsMatched: allMatched
      };
      
      if (allMatched) {
        // Remove from incomplete if it exists there
        setIncompleteWords(prev => prev.filter(r => r.word !== currentWord.Word));
        // Add to completed if not already there
        setCompletedWords(prev => {
          const exists = prev.some(r => r.word === currentWord.Word);
          if (exists) return prev;
          return [...prev, record];
        });
        // Award XP for completing a synonym word
        if (user) {
          supabase.rpc('record_session', {
            p_user_id: user.id,
            p_xp_earned: 2,
            p_correct: 1,
            p_answered: 1,
          }).then(({ error }) => {
            if (error) console.error('[XP] synonym record_session failed:', error.message);
          });
        }
      } else {
        // Remove from completed if it exists there
        setCompletedWords(prev => prev.filter(r => r.word !== currentWord.Word));
        // Add to incomplete if not already there
        setIncompleteWords(prev => {
          const exists = prev.some(r => r.word === currentWord.Word);
          if (exists) return prev;
          return [...prev, record];
        });
      }

        saveSynonymProgressToSupabase(currentWord, allMatched);
      recordCardSeen(currentWord.Word);
    }

    return allMatched;
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading words...</div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>No words loaded. Please check the CSV file.</div>
      </div>
    );
  }

  // If in review mode but no incomplete words, show message
  if (reviewMode && incompleteWords.length === 0) {
    return (
      <div style={{ padding: '20px', maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
        <h1 style={{ marginBottom: '20px' }}>Review Mode</h1>
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#d1ecf1', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <p style={{ fontSize: '18px', color: '#0c5460', margin: 0 }}>
            🎉 Great job! You have no incomplete synonym words to review.
          </p>
        </div>
        <button
          onClick={() => setReviewMode(false)}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Back to Regular Game
        </button>
      </div>
    );
  }

  if (!isSessionActive) {
    return (
      <div style={{ padding: '24px 16px', fontFamily: 'inherit' }}>
        <div style={{
          backgroundColor: '#1F4C40',
          borderRadius: '20px',
          padding: '32px 24px',
          textAlign: 'center',
          color: '#FFFFFF',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Session {sessionNumber}
          </div>
          {lastSessionCardCount > 0 && (
            <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 16 }}>
              Last session: {lastSessionCardCount} cards
            </div>
          )}
          <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 24 }}>
            {lifetimeSeenCount} / {words.length} words seen lifetime
          </div>
          {showPaywall && <PaywallModal feature="Unlimited Synonym sessions" onClose={() => setShowPaywall(false)} />}
          <button
            onClick={() => {
              if (!isPro && sessionNumber > FREE_SYNONYM_SESSION_LIMIT) {
                setShowPaywall(true);
              } else {
                startSession();
              }
            }}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F4C40',
              border: 'none',
              borderRadius: 999,
              padding: '14px 40px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {!isPro && sessionNumber > FREE_SYNONYM_SESSION_LIMIT ? '🔒 Upgrade to continue' : `Start session ${sessionNumber}`}
          </button>
          {!isPro && sessionNumber > FREE_SYNONYM_SESSION_LIMIT && (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 8, textAlign: 'center' }}>
              Free plan: {FREE_SYNONYM_SESSION_LIMIT} sessions/day
            </p>
          )}
        </div>
      </div>
    );
  }

  const currentWord = wordsToUse[currentWordIndex];
  const allSynonyms = getSynonyms();
  const allMatched = allSynonyms.length > 0 && allSynonyms.every(syn => matchedSynonyms.includes(syn.trim()));

  const getWordStyle = (word: string) => {
    const synonyms = getSynonyms();
    const isSynonym = synonyms.some(s => s.trim() === word.trim());
    const isSelected = selectedWords.includes(word.trim());
    const isMatched = showResult && matchedSynonyms.includes(word.trim());
    const isWrong = showResult && wrongSelections.includes(word.trim());
    const isMissed = showResult && isSynonym && !isMatched && !isSelected;
    
    const baseStyle: React.CSSProperties = {
      padding: '12px 8px',
      minHeight: '44px',
      fontSize: '13px',
      cursor: showResult ? 'default' : 'pointer',
      border: `1px solid var(--color-border)`,
      borderRadius: '12px',
      transition: 'background-color 0.15s ease, border-color 0.15s ease',
      textAlign: 'center',
      fontWeight: 500,
      backgroundColor: 'var(--color-surface-white)',
      color: 'var(--color-text)',
      boxShadow: 'none',
      width: '100%',
      fontFamily: 'var(--font-sans)',
    };

    // After checking answers
    if (showResult) {
      if (isMatched) {
        return {
          ...baseStyle,
          backgroundColor: 'var(--color-success-light)',
          borderColor: 'var(--color-success)',
          color: 'var(--color-success-text)',
        };
      } else if (isWrong) {
        return {
          ...baseStyle,
          backgroundColor: 'var(--color-danger-light)',
          borderColor: 'var(--color-danger)',
          color: 'var(--color-danger-text)',
        };
      } else if (isMissed) {
        return {
          ...baseStyle,
          backgroundColor: '#FFF8E6',
          borderColor: 'var(--color-amber)',
          color: '#7A5A00',
        };
      }
    }

    // Before checking - just show selection state
    if (isSelected) {
      return {
        ...baseStyle,
        backgroundColor: 'var(--color-success-light)',
        borderColor: 'var(--color-confirmation)',
        color: 'var(--color-primary)',
        fontWeight: 600,
      };
    }

    return baseStyle;
  };

  const totalAttempted = completedWords.length + incompleteWords.length;
  const completionRate = totalAttempted > 0 ? Math.round((completedWords.length / totalAttempted) * 100) : 0;

  const synonymGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginBottom: '12px',
    marginTop: '10px',
  };
  const timeOverviewStats = [
    { label: 'Total All Modes', value: formatTime(totalTime) },
    { label: 'Total Today', value: formatTime(totalTodayTime) },
    { label: 'Synonym Today', value: formatTime(todayTime) },
    { label: 'Total Yesterday', value: formatTime(getTotalYesterdayTime()) },
    { label: 'Total This Week', value: formatTime(getTotalThisWeekTime()) }
  ];

  const jumpDisabled = !jumpToNumber || isNaN(parseInt(jumpToNumber, 10)) || parseInt(jumpToNumber, 10) < 1 || parseInt(jumpToNumber, 10) > wordsToUse.length;

  return (
    <div style={{ backgroundColor: 'var(--color-background)', minHeight: '100vh', padding: '16px' }}>
      <Helmet>
        <title>GRE Synonym Matching – Practice All Synonyms | Phrenos</title>
        <meta name="description" content="Master GRE synonyms with an interactive matching game. Select every synonym for each word, track completion, and review incomplete sets. No login required." />
        <meta property="og:title" content="GRE Synonym Matching – Practice All Synonyms | Phrenos" />
        <meta property="og:description" content="Master GRE synonyms with an interactive matching game. Select every synonym for each word, track completion, and review incomplete sets." />
        <meta property="og:url" content="https://phrenos.app/synonyms" />
        <link rel="canonical" href="https://phrenos.app/synonyms" />
      </Helmet>
      <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>

        {/* Page title */}
        <div>
          <p className="eyebrow">Vocabulary</p>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--color-primary)' }}>
            {reviewMode ? 'Review synonyms' : 'Synonym matching'}
          </h1>
          <p style={{ margin: '2px 0 0', color: TEXT_MUTED, fontSize: '13px', fontWeight: 400, lineHeight: 1.6 }}>
            Select every synonym for each word
          </p>
        </div>

        {/* Stats row — single line */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {totalAttempted > 0 && (
            <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>
              <strong>{completedWords.length}/{totalAttempted}</strong>
              <span style={{ color: TEXT_MUTED }}> complete</span>
            </span>
          )}
          {totalAttempted > 0 && <span style={{ color: TEXT_MUTED, fontSize: '13px' }}>·</span>}
          {totalAttempted > 0 && (
            <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>
              <strong>{completionRate}%</strong>
              <span style={{ color: TEXT_MUTED }}> success</span>
            </span>
          )}
          {totalAttempted > 0 && <span style={{ color: TEXT_MUTED, fontSize: '13px' }}>·</span>}
          <button
            onClick={() => { setIsShuffled(!isShuffled); setCurrentWordIndex(0); }}
            className={`ds-chip${isShuffled ? ' active' : ''}`}
          >
            {isShuffled ? 'Shuffled' : 'Ordered'}
          </button>
          {incompleteWords.length > 0 && (
            <>
              <span style={{ color: TEXT_MUTED, fontSize: '13px' }}>·</span>
              <button
                onClick={() => setReviewMode(!reviewMode)}
                className={`ds-chip${reviewMode ? ' active' : ''}`}
              >
                {reviewMode ? 'Exit review' : `Review (${incompleteWords.length})`}
              </button>
            </>
          )}
        </div>

        {/* Main card */}
        <div style={{
          border: `1px solid ${BORDER}`,
          borderRadius: '20px',
          padding: '16px',
          backgroundColor: SURFACE,
        }}>
          {/* Word pill */}
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <span className="word-pill">
              {currentWord?.Word}
            </span>
          </div>

          <p style={{ margin: '0 0 8px', textAlign: 'center', color: TEXT_MUTED, fontSize: '12px' }}>
            Select all synonyms · {allSynonyms.length} to find · {selectedWords.length} selected
          </p>

          {/* Definition — shown after submit */}
          {showResult && (
            <p style={{ fontSize: '14px', color: TEXT_MUTED, margin: '0 0 10px', fontStyle: 'italic', textAlign: 'center' }}>
              {currentWord?.Definition}
            </p>
          )}

          {/* Result block — inside card, replaces separate result cards */}
          {showResult && (
            <div style={{
              padding: '10px 14px',
              marginBottom: '12px',
              borderRadius: '16px',
              backgroundColor: allMatched ? 'var(--color-success-light)' : 'var(--color-danger-light)',
              border: `1px solid ${allMatched ? 'var(--color-success)' : 'var(--color-danger)'}`,
            }}>
              <p style={{
                margin: '0 0 6px',
                fontSize: '13px',
                fontWeight: 700,
                color: allMatched ? 'var(--color-success-text)' : 'var(--color-danger-text)',
              }}>
                {allMatched ? '✓' : '✗'} {matchedSynonyms.length}/{allSynonyms.length} correct
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {matchedSynonyms.map((syn, idx) => (
                  <span key={idx} style={{
                    padding: '3px 10px',
                    backgroundColor: 'var(--color-success)',
                    color: '#fff',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}>{syn}</span>
                ))}
                {allSynonyms
                  .filter(syn => !matchedSynonyms.includes(syn.trim()))
                  .map((syn, idx) => (
                    <span key={idx} style={{
                      padding: '3px 10px',
                      backgroundColor: 'var(--color-danger)',
                      color: '#fff',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}>{syn}</span>
                  ))}
              </div>
            </div>
          )}

          {/* Synonym grid */}
          {wordOptions.length > 0 && (
            <div style={synonymGridStyle}>
              {wordOptions.map((word, idx) => {
                const isMatched = matchedSynonyms.includes(word.trim());
                return (
                  <button
                    key={idx}
                    onClick={() => handleWordSelect(word)}
                    style={getWordStyle(word)}
                    disabled={showResult}
                    onMouseEnter={(e) => {
                      if (!showResult && !isMatched && !wrongSelections.includes(word.trim())) {
                        e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!showResult && !isMatched && !wrongSelections.includes(word.trim())) {
                        e.currentTarget.style.backgroundColor = selectedWords.includes(word.trim()) ? 'var(--color-success-light)' : 'var(--color-surface-white)';
                      }
                    }}
                  >
                    {word}
                  </button>
                );
              })}
            </div>
          )}

          {/* Card footer: progress bar + position + jump */}
          <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: '10px', marginTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
              <span style={{ fontSize: '11px', color: TEXT_MUTED }}>
                {currentWordIndex + 1} / {wordsToUse.length}
                {reviewMode && <span style={{ color: 'var(--color-danger)' }}> · {incompleteWords.length} incomplete</span>}
              </span>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <input
                  type="number"
                  min="1"
                  max={wordsToUse.length}
                  value={jumpToNumber}
                  onChange={e => setJumpToNumber(e.target.value)}
                  onKeyPress={handleJumpInputKeyPress}
                  placeholder="#"
                  style={{
                    padding: '3px 6px',
                    fontSize: '12px',
                    width: '52px',
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                />
                <button
                  onClick={jumpToCard}
                  disabled={jumpDisabled}
                  style={{
                    padding: '3px 10px',
                    fontSize: '12px',
                    borderRadius: '999px',
                    border: 'none',
                    backgroundColor: jumpDisabled ? 'var(--color-border)' : PRIMARY,
                    color: '#fff',
                    cursor: jumpDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  Go
                </button>
              </div>
            </div>
            <div style={{ height: '3px', backgroundColor: 'var(--color-border)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${((currentWordIndex + 1) / wordsToUse.length) * 100}%`,
                backgroundColor: PRIMARY,
                borderRadius: '2px',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        </div>

        {/* Check Answers — only when not yet submitted and something is selected */}
        {!showResult && selectedWords.length > 0 && (
          <button
            onClick={checkCompletion}
            style={{
              width: '100%',
              padding: '13px',
              fontSize: '15px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: 'var(--color-text)',
              border: '1.5px solid #E0E0E0',
              borderRadius: '999px',
              fontWeight: 400,
            }}
          >
            Check answers
          </button>
        )}

        {/* Next Word — always visible, full width */}
        <button
          onClick={nextWord}
          style={{
            width: '100%',
            padding: '13px',
            fontSize: '15px',
            cursor: 'pointer',
            backgroundColor: PRIMARY,
            color: '#fff',
            border: 'none',
            borderRadius: '999px',
            fontWeight: 700,
          }}
        >
          Next word
        </button>

        {/* More — Time Overview + Progress Summary collapsed */}
        <Accordion title="More" defaultOpenDesktop={false} defaultOpenMobile={false}>
          <div style={{ marginBottom: '16px' }}>
            <p className="eyebrow" style={{ marginBottom: '8px' }}>Time overview</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
              {timeOverviewStats.map(stat => (
                <div key={stat.label} style={{
                  backgroundColor: 'var(--color-surface-white)',
                  border: `1px solid ${BORDER}`,
                  borderRadius: '16px',
                  padding: '8px 10px',
                  textAlign: 'center',
                }}>
                  <p style={{ margin: 0, fontSize: '10px', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{stat.label}</p>
                  <strong style={{ fontSize: '13px', color: 'var(--color-text)', fontWeight: 700 }}>{stat.value}</strong>
                </div>
              ))}
            </div>
          </div>

          {(completedWords.length > 0 || incompleteWords.length > 0) && (
            <div style={{ marginBottom: '16px' }}>
              <p className="eyebrow" style={{ marginBottom: '8px' }}>Progress summary</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <h4 style={{ color: '#15803d', marginBottom: '6px', fontSize: '13px' }}>Completed ({completedWords.length})</h4>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {completedWords.map((record, idx) => (
                      <span key={idx} style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, backgroundColor: '#f1fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                        {record.word}
                      </span>
                    ))}
                    {completedWords.length === 0 && <p style={{ color: TEXT_MUTED, fontStyle: 'italic', fontSize: '12px', margin: 0 }}>None yet</p>}
                  </div>
                </div>
                <div>
                  <h4 style={{ color: 'var(--color-danger)', marginBottom: '6px', fontSize: '13px' }}>Incomplete ({incompleteWords.length})</h4>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {incompleteWords.map((record, idx) => (
                      <span key={idx} style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, backgroundColor: 'var(--color-danger-light)', border: '1px solid var(--color-wrong-border)', color: 'var(--color-danger-text)' }}>
                        {record.word}
                      </span>
                    ))}
                    {incompleteWords.length === 0 && <p style={{ color: TEXT_MUTED, fontStyle: 'italic', fontSize: '12px', margin: 0 }}>None yet</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleClearProgress}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              cursor: 'pointer',
              backgroundColor: 'var(--color-danger)',
              color: 'white',
              border: 'none',
              borderRadius: '999px',
              fontWeight: 700,
            }}
          >
            Clear Progress
          </button>
        </Accordion>

        {/* Report Modal */}
        {showReportModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, padding: '20px',
          }}>
            <div style={{
              backgroundColor: 'var(--color-surface-white)',
              borderRadius: '8px', padding: '24px',
              maxWidth: '500px', width: '100%',
              maxHeight: '90vh', overflowY: 'auto',
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '14px', fontSize: '18px' }}>Report Incorrect Synonym</h3>
              <p style={{ marginBottom: '12px', color: TEXT_MUTED }}>
                Word: <strong>{wordsToUse[currentWordIndex]?.Word}</strong>
              </p>
              <p style={{ marginBottom: '12px', color: TEXT_MUTED }}>
                Reported Synonym: <strong>{reportingSynonym}</strong>
              </p>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                What's wrong? (Optional)
              </label>
              <textarea
                value={reportNote}
                onChange={(e) => setReportNote(e.target.value)}
                placeholder="Please describe why this synonym is incorrect..."
                style={{
                  width: '100%', minHeight: '80px', padding: '10px',
                  border: '1px solid #ccc', borderRadius: '4px',
                  fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', marginBottom: '16px',
                }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowReportModal(false); setReportNote(''); setReportingSynonym(''); }}
                  style={{ padding: '9px 18px', fontSize: '14px', cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitReport}
                  style={{ padding: '9px 18px', fontSize: '14px', cursor: 'pointer', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SynonymGame;
